import type { ContractValidationResult } from "./actionflow-contract-validators";
import type { ActionFlowTopologySimulationProof } from "./actionflow-topology-simulation";
import type { ActionFlowUserOutcomeProof } from "./actionflow-user-outcome-proof";
import type { ActionFlowBrowserTraceProof } from "./actionflow-browser-trace-proof";

export const ACTIONFLOW_READINESS_PACKET_SCHEMA_VERSION =
  "actionflow-readiness-packet.v1";

export type ActionFlowReadinessStageId =
  | "topology-validation"
  | "runtime-simulation"
  | "user-outcome"
  | "browser-trace";

export interface ActionFlowReadinessStage {
  id: ActionFlowReadinessStageId;
  status: "ready" | "blocked" | "missing";
  blocking: boolean;
  summary: string;
  failures: string[];
  evidenceRefs: string[];
}

export interface ActionFlowReadinessPacket {
  schemaVersion: typeof ACTIONFLOW_READINESS_PACKET_SCHEMA_VERSION;
  status: "ready" | "blocked";
  publishAllowed: boolean;
  stages: ActionFlowReadinessStage[];
  blockingFailures: string[];
  evidenceRefs: string[];
  policy: {
    invalidTopologyBlocksPublish: true;
    simulationFailureBlocksPublish: true;
    unmetUserOutcomeBlocksPublish: true;
    browserReflectionFailureBlocksPublish: true;
  };
}

export interface BuildActionFlowReadinessPacketInput {
  validationResult?: ContractValidationResult;
  simulationProof?: ActionFlowTopologySimulationProof;
  userOutcomeProof?: ActionFlowUserOutcomeProof;
  browserTraceProof?: ActionFlowBrowserTraceProof;
}

export function buildActionFlowReadinessPacket(
  input: BuildActionFlowReadinessPacketInput,
): ActionFlowReadinessPacket {
  const stages = [
    topologyValidationStage(input.validationResult),
    simulationStage(input.simulationProof),
    userOutcomeStage(input.userOutcomeProof),
    browserTraceStage(input.browserTraceProof),
  ];
  const blockingFailures = stages.flatMap((stage) =>
    stage.blocking ? stage.failures : [],
  );
  return {
    schemaVersion: ACTIONFLOW_READINESS_PACKET_SCHEMA_VERSION,
    status: blockingFailures.length === 0 ? "ready" : "blocked",
    publishAllowed: blockingFailures.length === 0,
    stages,
    blockingFailures,
    evidenceRefs: stages.flatMap((stage) => stage.evidenceRefs),
    policy: {
      invalidTopologyBlocksPublish: true,
      simulationFailureBlocksPublish: true,
      unmetUserOutcomeBlocksPublish: true,
      browserReflectionFailureBlocksPublish: true,
    },
  };
}

function topologyValidationStage(
  validationResult: ContractValidationResult | undefined,
): ActionFlowReadinessStage {
  if (!validationResult) return missingStage("topology-validation");
  return {
    id: "topology-validation",
    status: validationResult.ok ? "ready" : "blocked",
    blocking: !validationResult.ok,
    summary: validationResult.ok
      ? "Topology contract is valid."
      : "Topology contract is invalid.",
    failures: validationResult.errors,
    evidenceRefs: ["validateActionFlowTopologyContract"],
  };
}

function simulationStage(
  simulationProof: ActionFlowTopologySimulationProof | undefined,
): ActionFlowReadinessStage {
  if (!simulationProof) return missingStage("runtime-simulation");
  return {
    id: "runtime-simulation",
    status: simulationProof.ok ? "ready" : "blocked",
    blocking: !simulationProof.ok,
    summary: simulationProof.ok
      ? "Runtime simulation proof is ready."
      : "Runtime simulation proof failed.",
    failures: [...simulationProof.validationErrors, ...simulationProof.errors],
    evidenceRefs: ["actionFlowTopologySimulationProof"],
  };
}

function userOutcomeStage(
  userOutcomeProof: ActionFlowUserOutcomeProof | undefined,
): ActionFlowReadinessStage {
  if (!userOutcomeProof) return missingStage("user-outcome");
  return {
    id: "user-outcome",
    status: userOutcomeProof.ok ? "ready" : "blocked",
    blocking: !userOutcomeProof.ok,
    summary: userOutcomeProof.ok
      ? "User outcome proof is ready."
      : "User outcome proof failed.",
    failures: userOutcomeProof.blockingFailures,
    evidenceRefs: ["actionFlowUserOutcomeProof"],
  };
}

function browserTraceStage(
  browserTraceProof: ActionFlowBrowserTraceProof | undefined,
): ActionFlowReadinessStage {
  if (!browserTraceProof) return missingStage("browser-trace");
  return {
    id: "browser-trace",
    status: browserTraceProof.ok ? "ready" : "blocked",
    blocking: !browserTraceProof.ok,
    summary: browserTraceProof.ok
      ? "Browser trace proof is ready."
      : "Browser trace proof failed.",
    failures: browserTraceProof.blockingFailures,
    evidenceRefs: ["actionFlowBrowserTraceProof"],
  };
}

function missingStage(id: ActionFlowReadinessStageId): ActionFlowReadinessStage {
  return {
    id,
    status: "missing",
    blocking: true,
    summary: `${id} proof is missing.`,
    failures: [`${id} proof is missing.`],
    evidenceRefs: [],
  };
}
